import {
  Action,
  combineReducers,
  createFeatureSelector,
  createSelector
} from '@ngrx/store';
import { TrustTaxCategory } from '../../shared/types/TrustTaxCategory';
import { ApiEndpointType, cashieringIraAgreementText, Constants, distributionReasons, fedTaxErrorMap, Frequencies, frequencyDict, pendReasonsStrings, RegTypeCode, submitAndCreateConfirmationMessages, submitAndCreateWarnings, transactionDirection, transactionTypes, yesNo } from '../../shared/utilities/Constants';
import { getFilterDate } from '../components/transactions-table/table-helpers';
import { isAchExtraPrompts, isAchForm, isAchTransactionDetails, isCheckExtraPrompts, isCheckTransactionDetails, isItExtraPrompts, isItTransactionDetails, isWireExtraPrompts, isWireTransactionDetails, ItExtraPrompts, prefilledFlowNames, prefilledFlowStates, TaxWithholdingFields } from '../models/form/TransactionForm';
import { EAuthModel } from '../models/internal-transfer/EAuthModel';
import * as fromAch from './ach.reducers';
import * as fromCheck from './check.reducers';
import * as fromInternalTransfer from './internal-transfer.reducers';
import * as fromInternalTransfers from './internal-transfer-list.reducers';
import * as fromPostionLotsTableState from './positions-lots-table-state.reducers';
import * as fromPtrsInfo from './ptrs-info.reducers';
import * as fromStagedTransactionFormState from './staged-transaction-form.reducer';
import * as fromTransactionFormState from './transaction-form.reducer';
import * as fromTransactionsTableState from './transactions-table-state.reducers';
import * as fromTrustInfo from './trust-info.reducers';
import * as fromTwePopup from './twe-popup.reducers';
import * as fromTweTableStateReducer from './twe-table-state.reducer';
import * as fromWire from './wire.reducers';
import * as fromTransactionOptions from './transaction-options.reducer';
import * as fromSaveAsTemplateModal from './save-as-template-modal.reducer';
import * as fromMasterTransactionsTableState from './master-transactions-table-state.reducer';
import * as fromMasterTransactions from './master-transactions-reducer';
import * as fromActivityReport from './activity-report.reducer';
import { selectEAuthAccountHolderSelected, selectEAuthSelectedAccountHolderIds } from '../../shared/reducers';
import { selectCreateStandingAuthEAuthClients, selectEAuthClients } from '../../setup-eAuthorization/reducers';
import { WarningOrError } from '../models/shared/WarningOrError';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement } from '../../authorize/reducers';
import { selectFeatureFlags } from '../../feature-flags/reducers';
import { EAuthModelCustomer } from '../models/internal-transfer/EAuthModelCustomer';
import { Revalidation } from '../models/shared/Revalidation';
import { DistributionReason } from '../models/internal-transfer/DistributionReason';
import { selectStandingAuthorization } from '../../standing-authorizations/reducers';

export const createRequestFeatureKey = 'createRequest';

export interface CreateRequestState {
  [fromTrustInfo.trustInfoKey]: fromTrustInfo.State;
  [fromTransactionsTableState.transactionsTableStateKey]: fromTransactionsTableState.State;
  [fromPostionLotsTableState.positionsLotsTableStateKey]: fromPostionLotsTableState.State;
  [fromTweTableStateReducer.tweTableStateKey]: fromTweTableStateReducer.State;
  [fromTransactionFormState.transactionFormKey]: fromTransactionFormState.State;
  [fromStagedTransactionFormState.stagedFormKey]: fromStagedTransactionFormState.State;
  [fromPtrsInfo.ptrsInfoKey]: fromPtrsInfo.State;
  [fromTwePopup.twePopupKey]: fromTwePopup.State;
  [fromWire.wireInfoKey]: fromWire.State;
  [fromAch.achInfoKey]: fromAch.State;
  [fromInternalTransfer.internalTransferInfoKey]: fromInternalTransfer.State;
  [fromInternalTransfers.internalTransfersInfoKey]: fromInternalTransfers.State;
  [fromCheck.checkInfoKey]: fromCheck.State;
  [fromTransactionOptions.transactionOptionsKey]: fromTransactionOptions.State;
  [fromSaveAsTemplateModal.saveAsTemplateModalKey]: fromSaveAsTemplateModal.State;
  [fromMasterTransactionsTableState.masterTransactionsTableStateKey]: fromMasterTransactionsTableState.State;
  [fromMasterTransactions.masterTransactionsKey]: fromMasterTransactions.State;
  [fromActivityReport.activityReportKey]: fromActivityReport.State;
}

export function reducers(
  state: CreateRequestState | undefined,
  action: Action
) {
  return combineReducers({
    [fromTransactionsTableState.transactionsTableStateKey]: fromTransactionsTableState.transactionsTableStateReducer,
    [fromPostionLotsTableState.positionsLotsTableStateKey]: fromPostionLotsTableState.positionsLotsTableStateReducer,
    [fromAch.achInfoKey]: fromAch.achReducer,
    [fromTrustInfo.trustInfoKey]: fromTrustInfo.reducer,
    [fromInternalTransfer.internalTransferInfoKey]: fromInternalTransfer.internalTransferReducer,
    [fromInternalTransfers.internalTransfersInfoKey]: fromInternalTransfers.internalTransfersReducer,
    [fromTransactionFormState.transactionFormKey]: fromTransactionFormState.transactionFormReducer,
    [fromStagedTransactionFormState.stagedFormKey]: fromStagedTransactionFormState.reducer,
    [fromTweTableStateReducer.tweTableStateKey]: fromTweTableStateReducer.tweTableStateReducer,
    [fromPtrsInfo.ptrsInfoKey]: fromPtrsInfo.reducer,
    [fromWire.wireInfoKey]: fromWire.wireReducer,
    [fromCheck.checkInfoKey]: fromCheck.checkReducer,
    [fromTwePopup.twePopupKey]: fromTwePopup.reducer,
    [fromTransactionOptions.transactionOptionsKey]: fromTransactionOptions.reducer,
    [fromSaveAsTemplateModal.saveAsTemplateModalKey]: fromSaveAsTemplateModal.reducer,
    [fromMasterTransactionsTableState.masterTransactionsTableStateKey]: fromMasterTransactionsTableState.masterTransactionsTableStateReducer,
    [fromMasterTransactions.masterTransactionsKey]: fromMasterTransactions.masterTransactionsReducer,
    [fromActivityReport.activityReportKey]: fromActivityReport.activityReportReducer,
  })(state, action);
}

export const selectCreateRequest = createFeatureSelector<CreateRequestState>(
  createRequestFeatureKey
);

export const selectTransactionsTableState = createSelector(
  selectCreateRequest,
  (state) => {
    return state.transactionsTableState;
  }
);

export const selectPositionsLotsTableState = createSelector(
  selectCreateRequest,
  (state) => {
    return state?.positionsLotsTableState ?? fromPostionLotsTableState.intialPositionsLotsTableState;
  }
);

export const selectMasterTransactionsTableState = createSelector(
  selectCreateRequest,
  (state) => {
    return state.masterTransactionsTableState;
  }
);

export const selectMasterTransactions = createSelector(
  selectCreateRequest,
  (state) => {
    return state.masterTransactions;
  }
);

export const selectMasterTransactionStatusData = createSelector(
  selectMasterTransactions,
  (state) => {
    return state.transactions;
  }
);

export const selectActivityReportTableState = createSelector(
  selectCreateRequest,
  (state) => {
    return state.activityReport;
  }
);

export const selectActivityReports = createSelector(
  selectActivityReportTableState,
  (state) => {
    return state.activityReports;
  }
)

export const selectBookmarkedFilterStatus = createSelector(
  selectMasterTransactions,
  (state) => {
    return state.isBookmarkFilterOn;
  }
);

export const selectInternalTransferInfo = createSelector(
  selectCreateRequest,
  (state) => {
    return state?.internalTransferInfo ?? fromInternalTransfer.initialInternalTransferInfoState;
  }
);

export const selectInternalTransfersInfo = createSelector(
  selectCreateRequest,
  (state) => {
    return state?.internalTransfersInfo ?? fromInternalTransfers.initialInternalTransfersInfoState;
  }
);

export const selectInputIraFields = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.inputIraFields;
  }
);

export const selectITDistributionReason = createSelector(
  selectInputIraFields,
  (state) => {
    return state.distributionReason;
  }
);

export const selectIsIraTransaction = createSelector(
  selectInputIraFields,
  (state) => {
    return state.isIraTransaction;
  }
);
export const selectIraFieldsIsIraTransaction = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state?.iraFields.isIraTransaction;
  }
);
export const selectITDistributionReasonMap = createSelector(
  selectInputIraFields,
  (state) => {
    return state.distributionReasonMap;
  }
);

export const selectProcessDate = createSelector(selectInternalTransferInfo,
  (state) => {
    return state.processDate;
  }
);

export const selectItStwProcessCode = createSelector(selectInternalTransferInfo,
  (state) => {
    return state?.iraFields.stwProcessCode;
  }
);

export const selectToAccountsState = createSelector(
  selectInternalTransfersInfo,
  (state) => {
    return state.toAccountState;
  }
);

export const selectToAccountState = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.toAccountState;
  }
);

export const selectInternalTransferServiceError = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.serviceError;
  }
);

export const selectInternalTransfersHasServiceError = createSelector(
  selectInternalTransfersInfo,
  (state) => {
    return state.hasServiceError;
  }
);

export const selectTransactionForm = createSelector(
  selectCreateRequest,
  (state) => {
    return state?.transactionForm ?? fromTransactionFormState.initialTransactionFormState;
  }
);

export const selectTransactionFormPrefilledState = createSelector(selectTransactionForm, (state) => {
  return state.prefilledFlow
})

export const selectShowCashieringIraAgreement = createSelector(
  selectTransactionForm,
  (state) => {
    return state.showCashieringIRAAgreement;
  }
);

export const selectTransactionFormLoadingState = createSelector(
  selectTransactionForm,
  (state) => {
    return state.loading;
  }
);

export const selectStagedTransactionForm = createSelector(
  selectCreateRequest,
  (state) => {
    return state.stagedTransactionForm;
  }
);

export const selectMultipleToJournalsList = createSelector(
  selectCreateRequest,
  (state) => {
    return state.internalTransfersInfo.multipleToJournalsList;
  }
);

export const selectStagedTransactionFormLoadingState = createSelector(
  selectStagedTransactionForm,
  (state) => {
    return state.loading;
  }
);

export const selectToAccountPtrs = createSelector(
  selectStagedTransactionForm,
  (state) => {
    return (state.transactionDetails as any)?.toAccounts ?? [];
  });

export const selectCostBasis = createSelector(
  selectCreateRequest,
  (state) => {
    return state?.transactionForm.transactionDetails.costBasisMethod;
  }
);

export const selectStagedCostBasisMethod = createSelector(
  selectStagedTransactionForm,
  (state) => {
    return state.transactionDetails.costBasisMethod;
  }
);

export const selectStagedStandingAuthid = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && state?.extraPrompts) {
      return state?.extraPrompts.standingAuthId || '';
    }
    return '';
  }
);

export const selectStagedRequestingAccountHolderId = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && state?.extraPrompts && isWireExtraPrompts(state?.extraPrompts)) {
      return state?.extraPrompts.requestingAccountHolderId || '';
    }
  }
);

export const selectContinueClickTotalDistTriggered = createSelector(
  selectStagedTransactionForm,
  (state) => {
    return state.continueClickTotalDistTriggered;
  }
);

export const selectCostBasisMethod = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.positionsLotDetails.costMethod;
  }
);

export const selectPositionLotTableData = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.rowData;
  }
);

export const selectMultipleToAccountsPositionLotTableData = createSelector(
  selectInternalTransfersInfo,
  (state) => {
    return state.rowData;
  }
)

export const selectPositionLotDetails = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.positionsLotDetails;
  }
);

export const selectPositionDetails = createSelector(
  selectInternalTransfersInfo,
  (state) => {
    return state.positionsLotDetails;
  }
);

export const selectTransactionType = createSelector(
  selectTransactionForm,
  (state) => {
    return state.transactionType;
  }
);

export const selectItMultipleToAccountsUsed = createSelector(
  selectInternalTransfersInfo,
  selectTransactionType,
  (info, type) => {
    return (type === transactionTypes.internalTransfer) && Object.keys(info.toAccountInfo).length > 1;
  }
);

export const selectCreateTransactionInProgress = createSelector(
  selectTransactionForm,
  (state) => {
    return state.createTransactionInProgress;
  }
);

export const selectStagedTransactionType = createSelector(
  selectStagedTransactionForm,
  (state) => {
    return state.transactionType;
  }
);

export const selectStagedTransactionFormItExtraPrompts = createSelector(
  selectStagedTransactionForm, selectTransactionType,
  (state, transactionType) => {
    if (transactionType === transactionTypes.internalTransfer) {
      return state?.extraPrompts as ItExtraPrompts;
    }
  }
);

export const selectTransactionFormExtraPrompts = createSelector(
  selectTransactionForm,
  (state) => {
    return state?.extraPrompts;
  }
);

export const selectItIsRmdEligible = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.isRMDEligible;
  }
);


export const selectItIsIdentical = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.isIdentical;
  }
);

export const selectItInputCallSuccessful = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.inputCallSuccessful;
  }
);

export const selectIsInputCallFinished = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.inputCallFinished;
  }
)

export const selectIsInputandPositionsCallFinished = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.inputCallFinished && state.positionLotCallFinished;
  }
)

export const selectIsRecurring = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('frequency' in state && state.frequency) {
      return state.frequency === Frequencies.recurring;
    } else if ('useBundledStandingAuth' in state) {
      return state.useBundledStandingAuth === 'recurring';
    }
  }
  return false;
});

export const selectIsOneTimeTotalDist = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('frequency' in state && state.frequency) {
      return state.frequency == 'onetimeTotalDist';
    }
  }
  return false;
});

export const selectIsSloaOneTimeTotalDist = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('useStandingAuth' in state && state.useStandingAuth === yesNo.yes && 'frequency' in state && state.frequency) {
      return state.frequency === 'onetimeTotalDist';
    }
  }
  return false;
});

export const selectIsBundledOneTimeTotalDist = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('useBundledStandingAuth' in state && 'frequency' in state && state.frequency) {
      return state.frequency === 'onetimeTotalDist';
    }
    if ('useBundledStandingAuth' in state && state.useBundledStandingAuth) {
      return state.useBundledStandingAuth === 'onetimeTotalDist';
    }
  }
  return false;
});

export const selectIsOneTimeCloseAccount = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('closeAccount' in state && state.closeAccount) {
      return state.closeAccount == 'yes';
    }
  }
  return false
});

export const selectCloseAccountTaxForms = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('taxForms' in state && state.taxForms) {
      return state.taxForms;
    }
  }
  return false
});

export const selectCloseAccountAccountStatments = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('accountStatements' in state && state.accountStatements) {
      return state.accountStatements;
    }
  }
  return false
});

export const selectCloseAccountTradeConfirmations = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('tradeConfirmations' in state && state.tradeConfirmations) {
      return state.tradeConfirmations;
    }
  }
  return false
});

export const selectTaxFormsYearInput = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('taxFormsDateInputControl' in state && state.taxFormsDateInputControl) {
      return state.taxFormsDateInputControl;
    }
  }
  return false
})

export const selectAccountStatementsDateInput = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('accountStatementsDateInputControl' in state && state.accountStatementsDateInputControl) {
      return state.accountStatementsDateInputControl;
    }
  }
  return false
})

export const selectTradeConfirmationsDateInput = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state) {
    if ('tradeConfirmationsDateInputControl' in state && state.tradeConfirmationsDateInputControl) {
      return state.tradeConfirmationsDateInputControl;
    }
  }
  return false
})

export const selectIsPositionsCash = createSelector(selectTransactionFormExtraPrompts, (state) => {
  if (state && 'transferType' in state) {
    return state.transferType === 'positions' || state.transferType === 'positionsCash';
  }
  return false;
});

export const selectTransactionFormTransactionDetails = createSelector(
  selectTransactionForm,
  (state) => {
    return state.transactionDetails;
  }
);

export const selectABA = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (isWireTransactionDetails(state)) {
      return state.aba;
    }
  }
);

export const selectPayIncome = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (isItTransactionDetails(state)) {
      return state.recurringFieldsFormGroup?.payIncome
    }
  }
);

export const selectCheckPayIncome = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (isCheckTransactionDetails(state)) {
      return state?.payIncome ?? '';
    }
    return '';
  }
);

export const selectModifiedPayeeCheckbox = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (isCheckTransactionDetails(state) || isWireTransactionDetails(state)) {
      return state.modifiedPayeeCheckbox;
    }
    return false;
  }
);

export const selectWireModifiedPayeeName1 = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (isWireTransactionDetails(state)) {
    return state.modifiedPayeeName1;
  }
})

export const selectWireModifiedPayeeName2 = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (isWireTransactionDetails(state)) {
    return state.modifiedPayeeName2;
  }
})

export const selectWireIsoModifiedPayeeName = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (isWireTransactionDetails(state)) {
    return state.isoModifiedPayeeName;
  }
})

export const selectCompletionDate = createSelector(
  selectTransactionForm,
  (state) => {
    if (isAchForm(state)) {
      return state.transactionDetails.completionDate;
    }
    return '';
  }
);

export const selectWithdrawalDate = createSelector(
  selectTransactionForm,
  (state) => {
    if (isAchForm(state)) {
      return state.transactionDetails.withdrawalDate;
    }
    return '';
  }
);

export const selectContributionReason = createSelector(
  selectTransactionForm,
  (state) => {
    if (isAchForm(state)) {
      return state.transactionDetails.contributionReason;
    }
    return '';
  }
);

export const selectTransactionFormServiceError = createSelector(
  selectTransactionForm,
  (state) => {
    return state.serviceError;
  }
)

export const selectTrustTaxCategorySelected = createSelector(
  selectTransactionForm,
  (state) => {
    return state.transactionDetails.trustTaxCategory
  }
);

export const selectTransactionFormHasError = createSelector(selectTransactionForm, state => state.hasError);

export const selectTransactionFormCorrId = createSelector(
  selectTransactionFormServiceError,
  (state) => {
    return state.correlationId;
  }
)

export const selectTransactionFormDirection = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isAchExtraPrompts(state)) {
      return state.transferDirection;
    }
    return '';
  }
)

export const selectFundsAvailableToTrade = createSelector(
  selectTransactionForm,
  selectTransactionFormDirection,
  (state, direction) => {
    if (isAchForm(state) && direction === transactionDirection.in.toLowerCase()) {
      return state.transactionDetails.fundsAvailableToTrade;
    }
    return '';
  }
);

export const selectSettlementDate = createSelector(
  selectTransactionForm,
  selectTransactionFormDirection,
  (state, direction) => {
    if (isAchForm(state) && direction === transactionDirection.in.toLowerCase()) {
      return state.transactionDetails.settlementDate;
    }
    return '';
  }
);

export const selectUseStandingAuth = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && (isItExtraPrompts(state) || isWireExtraPrompts(state) || isCheckExtraPrompts(state))) {
      if (state.useStandingAuth === 'bundled') {
        return state.useBundledStandingAuth === 'yes';
      }
      return state?.useStandingAuth?.toLowerCase() === 'yes';
    }
    return false;
  }
);

export const selectIsBundled = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && (isWireExtraPrompts(state) || isCheckExtraPrompts(state) || isItExtraPrompts(state))) {
      return state?.useStandingAuth?.toLowerCase() === 'bundled';
    }
    return false;
  }
);

export const selectFrequency = createSelector(
  selectTransactionFormExtraPrompts,
  (state): "recurring" | "onetime" | "onetimeTotalDist" | undefined => {
    if (state && isAchExtraPrompts(state)) {
      return state?.frequency;
    }

    if (state && isItExtraPrompts(state)) {
      return state?.frequency;
    }

    if (state && isCheckExtraPrompts(state)) {
      return state?.frequency;
    }

    return 'onetime';
  }
);

export const selectTransferType = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isItExtraPrompts(state)) {
      return state?.transferType;
    }
    return false;
  }
);

export const selectStandingAuthId = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state) {
      return state.standingAuthId || '';
    }
    return '';
  }
);

export const selectTemplateId = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isWireExtraPrompts(state)) {
      return state.templateId;
    }
    return '';
  }
)

export const selectRequestingAccountHolderId = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && (isWireExtraPrompts(state) || isAchExtraPrompts((state)))) {
      return state.requestingAccountHolderId || '';
    }
    return '';
  }
);

export const selectWireUseStandingAuthVal = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isWireExtraPrompts(state)) {
      if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'yes') {
        return 'bundled-yes';
      }
      else if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'no') {
        return 'bundled-no';
      }
      else {
        return state.useStandingAuth || '';
      }
    }
    return '';
  }
);

export const selectJournalsUseStandingAuth = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isItExtraPrompts(state)) {
      if (state.useStandingAuth === 'bundled' && !!state.useBundledStandingAuth) {
        return `${state.useStandingAuth}-${state.useBundledStandingAuth}`;
      }
      return state.useStandingAuth || '';
    }
    return '';
  }
);

export const selectUseTemplateVal = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isWireExtraPrompts(state)) {
      return state.useTemplates || '';
    }
    return '';
  }
);

export const selectStagedUseTemplateVal = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && state.extraPrompts && isWireExtraPrompts(state.extraPrompts)) {
      return state.extraPrompts.useTemplates || '';
    }
    return '';
  }
)

export const selectUseTemplate = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isWireExtraPrompts(state)) {
      return state?.useTemplates?.toLowerCase() === 'yes';
    }
    return false;
  }
);

export const selectCheckUseStandingAuth = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isCheckExtraPrompts(state)) {
      if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'yes') {
        return 'bundled-yes';
      }
      else if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'no') {
        return 'bundled-no';
      }
      else if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'recurring') {
        return 'bundled-recurring';
      }
      else if (state.useStandingAuth === 'bundled' && state.useBundledStandingAuth === 'onetimeTotalDist') {
        return 'bundled-totalDistribution'
      }
      return state.useStandingAuth || '';
    }
    return '';
  }
);

export const selectCheckFrequency = createSelector(
  selectTransactionFormExtraPrompts,
  (state) => {
    if (state && isCheckExtraPrompts(state)) {
      return state.frequency || '';
    }
    return '';
  }
);

export const selectCheckSecondProcessDate = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (state && isCheckTransactionDetails(state)) {
      return state.secondProcessDate || '';
    }
    return '';
  }
);

export const selectCheckRecurringFrequency = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (state && isCheckTransactionDetails(state)) {
      return state.recurringFrequency || '';
    }
    return '';
  }
);

export const selectAchRecurringFrequency = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && isAchTransactionDetails(state.transactionDetails)) {
      return state.transactionDetails.recurringFrequency || '';
    }
    return '';
  }
);

export const selectCheckEndDate = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (state && isCheckTransactionDetails(state)) {
      return state.endDate || '';
    }
    return '';
  }
);

export const selectMultipleItAccountInfoBanner = createSelector(selectInternalTransfersInfo, (state) => {
  return state.accountInfoBanner;
});

export const selectMultipleItAvailableCash = createSelector(selectMultipleItAccountInfoBanner, (state) => {
  return state.availableCashMMFAmount;
})

export const selectAccountInfoBanner = createSelector(selectInternalTransferInfo, (state) => {
  return state.accountInfoBanner;
});

export const selectITAvailableCashMMF = createSelector(
  selectAccountInfoBanner, (state) => {
    return state.availableCashMMFAmount;
  })

export const selectITAvailableCashMargin = createSelector(
  selectAccountInfoBanner, (state) => {
    return state.availableOnMarginAmount
  });

export const selectToAccountInfo = createSelector(selectInternalTransferInfo, (state) => {
  return state.toAccountInfo;
});
export const selectIsIRAAccount = createSelector(selectInternalTransferInfo, (state) => {
  return state.toAccountInfo.isIraAccount;
});

export const selectToAccountsInfo = createSelector(selectInternalTransfersInfo, (state) => {
  return state.toAccountInfo;
});

export const selectIs401kToAccount = createSelector(selectToAccountsInfo, (state) => {
  return Object.values(state).some(info => (info?.registrationType === RegTypeCode.INDIVIDUAL_401K || info?.registrationType === RegTypeCode.ROTH_401K));;
});

export const selectAchInfo = createSelector(
  selectCreateRequest,
  (state) => {
    return state.achInfo;
  }
);

export const selectAchInfoDistributionReason = createSelector(
  selectAchInfo,
  (state) => {
    let reason: DistributionReason = {
      code: '',
      subCode: '',
      description: state.distributionReason,
      isTaxExempt: false
    }
    return reason;
  }
)

export const selectIsCreateAchProfile = createSelector(selectAchInfo, (state) => {
  return state.isCreateAchProfile;
});

export const selectAchInfoFrequencyList = createSelector(selectAchInfo, (state) => {
  return state.frequencyList;
});

export const selectAchInfoToFromAccount = createSelector(selectAchInfo, (state) => {
  return state.toFromAccount;
});

export const selectAchStwProcessCode = createSelector(selectAchInfo, (state) => {
  return state.stwProcessCode;
});

export const selectMoneyLinkType = createSelector(selectAchInfo, (state) => {
  return state.moneyLinkType;
});

export const selectFiAccountTypeCode = createSelector(selectAchInfo, (state) => {
  return state.fiAccountType;
});

export const selectStandingAuthCallSucceededAch = createSelector(
  selectAchInfo,
  (state) => {
    return state.standingAuthorizationsCallSucceeded;
  }
)

export const selectACHAvailableCashMMF = createSelector(
  selectAchInfo,
  (state) => {
    return state.availableCashMMF
  }
)

export const selectACHAvailableCashMargin = createSelector(
  selectAchInfo,
  (state) => {
    return state.availableCashMargin
  }
)

export const selectContributionOptions = createSelector(
  selectAchInfo,
  (state) => state ? state.contributionOptions : []
)

export const selectMaxTransferAmount = createSelector(
  selectAchInfo,
  (state) => state ? state.maxTransferAmount : ''
)

export const selectAchStandingAuthId = createSelector(
  selectAchInfo,
  (state) => {
    return state.cashieringRequestId;
  }
)

export const selectAchSelectedAuthorization = createSelector(
  selectTransactionForm,
  (state) => {
    return state.extraPrompts?.standingAuthId;
  }
)

export const selectAchTxnCode = createSelector(
  selectAchInfo,
  (state) => state ? state.txnCode : ''
)

export const selectInternalTransfersTxnCode = createSelector(selectInternalTransferInfo, (state) => state.txnCode);

export const selectTaxWithholdings = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (state?.taxWithholdingsFormGroup) {
      return state.taxWithholdingsFormGroup;
    }
    return {
      withholdFedTax: false,
      withholdStateTax: false,
      fedTaxPercent: '',
      fedTaxAmount: '',
      stateTaxPercent: '',
      stateTaxAmount: '',
      distributionReason: '',
      distributionReasonMap: []
    } as TaxWithholdingFields
  }
);

export const selectTranasctionFormProcessDate = createSelector(
  selectTransactionFormTransactionDetails,
  (state) => {
    if (state && isItTransactionDetails(state) || isCheckTransactionDetails(state)) {
      return (state).processDate;
    }
    return '';
  }
);

export const selectIsFromEdit = createSelector(selectTransactionFormPrefilledState, ({ state }) => {
  return state === 'inProgress';
});

export const selectIsFromEditSubmittedTransaction = createSelector(selectTransactionFormPrefilledState, (prefilledFormState) => {
  return prefilledFormState.name === prefilledFlowNames.editTable;
})

export const selectEditStartedTable = createSelector(selectTransactionFormPrefilledState, (state) => {
  return state.name === prefilledFlowNames.editTable && state.state === prefilledFlowStates.started;
})

export const selectEditFLowFromTableComplete = createSelector(selectTransactionFormPrefilledState, (prefilledFormState) => {
  return prefilledFormState.name === prefilledFlowNames.editTable && prefilledFormState.state === prefilledFlowStates.complete;
})

export const selectEditFlowNotInitiated = createSelector(selectTransactionFormPrefilledState, (state) => {
  return state.name === '' && state.state === '';
})

export const selectItTaxPercents = createSelector(selectInternalTransferInfo, (state) => {
  let taxWithholding: TaxWithholdingFields = {
    stateTaxPercent: state?.iraFields.stateTaxPercent,
    fedTaxPercent: state?.iraFields.fedTaxPercent,
    fedTaxAmount: '',
    stateTaxAmount: '',
    distributionReason: '',
    distributionReasonMap: []
  };
  return taxWithholding;
})

export const selectClientAuthNeeded = createSelector(selectInternalTransferInfo, (state) => {
  return state.clientAuthorizationNeeded;
});

export const selectTwe = createSelector(selectCreateRequest, (state) => {
  return state.twePopupInfo;
});

export const selectTweCashieringTransactions = createSelector(selectTwe, (state) => {
  return state.accountDetails.cashieringRequestAprs;
});

export const selectTweRecurringTransactions = createSelector(selectTwe, (state) => {
  return state.accountDetails.recurringRequests;
});

export const selectTweStandingAuth = createSelector(selectTwe, (state) => {
  return state.accountDetails.sloaRequests;
});

export const selectTwePopupIndicator = createSelector(selectTwe, (state) => {
  return state.openTwePopup;
});

export const selectTweUpcomingTransactions = createSelector(
  selectTweCashieringTransactions,
  selectTweRecurringTransactions,
  (cashieringTransactions, recurringTransactions) => {
    let transactions = cashieringTransactions.filter((val) => {
      let count = 0;
      const today = getFilterDate()
      const processDate = new Date(val.formattedDate)
      const resolvedStatus =
        val.cashrRequestStatCd === 'CANC' ||
        val.cashrRequestStatCd === 'INAC' ||
        val.cashrRequestStatCd === 'VOID' ||
        val.cashrRequestStatCd === 'COMP' ||
        val.cashrRequestStatCd === 'STOP' ||
        val.cashrRequestStatCd === 'FAIL';
      if (count < 500 &&
        (processDate >= today.onlyDate && !resolvedStatus && !val.typeDescription.includes('SLOA')) ||
        (val.typeDescription.includes('Recurring') && val.cashrRequestStatCd === 'PEND')) {
        count++;
        return true;
      } else {
        return false;
      }
    });
    let recTransactions = recurringTransactions.filter((val) => {
      const next15Day = getFilterDate(14)
      const today = getFilterDate()
      const processDate = new Date(val.formattedDate)
      const activeStatus =
        val.recurStatusCode === 'ACTV' ||
        val.recurStatusCode === 'PEND' ||
        val.recurStatusCode === 'APPR';
      return (
        processDate >= today.onlyDate &&
        processDate <= next15Day.onlyDate &&
        activeStatus
      );
    });
    return [...transactions, ...recTransactions]
  });

export const selectTweTableState = createSelector(selectCreateRequest, (state) => {
  return state.tweTableState
});

export const selectAchTaxWithholding = createSelector(selectAchInfo, (state) => {
  const taxWithholding: TaxWithholdingFields = {
    fedTaxPercent: state.fedTaxPercent,
    fedTaxAmount: state.fedTaxAmount,
    stateTaxPercent: state.stateTaxPercent,
    stateTaxAmount: state.stateTaxAmount,
    distributionReason: state.distributionReason,
    distributionReasonMap: state.distributionReasonsList,
    stateHasTaxes: state.withholdStateTax
  };
  return taxWithholding;
});

export const selectDefaultAchTaxWithholding = createSelector(selectAchInfo, (state) => {
  return {
    fedTaxPercent: state.defaultFedTaxPercent,
    stateTaxPercent: state.defaultStateTaxPercent,
    distributionReason: state.defaultDistributionReason,
    amountType: state.defaultAmountType
  };
});

export const selectAchInputFinished = createSelector(selectAchInfo, (state) => {
  return state.inputCallFinished;
});

export const selectAchVerifyFinished = createSelector(selectAchInfo, (state) => {
  return state.verifyCallFinished;
});

export const selectCashieringDateCallFinished = createSelector(selectAchInfo, (state) => {
  return state.cashieringDateCallFinished;
});

export const selectAchAmountType = createSelector(selectAchInfo, (state) => {
  return state.amountType;
});


export const selectAchHasError = createSelector(selectAchInfo, (state) => {
  return state.messageFields.hasError;
});

export const selectAchErrorList = createSelector(selectAchInfo, (state) => {
  return state.messageFields.errorList;
});

export const selectTransactionsTablePrint = createSelector(selectTransactionsTableState, (state) => {
  return state.printClicked;
});

export const selectTransactionTableDeleteTemplateError = createSelector(selectTransactionsTableState, (state) => {
  return state.deleteTemplateError;
});

export const selectTransactionsTableCurrentTab = createSelector(
  selectTransactionsTableState,
  (state) => {
    return state.currentTab;
  }
);

export const selectMasterTransactionsTableCurrentTab = createSelector(selectMasterTransactionsTableState, (state) => {
  return state.currentTab;
})

export const selectTransactionsTableCurrentTemplateId = createSelector(
  selectTransactionsTableState,
  (state) => {
    return state.currentTemplate;
  }
);

export const selectTrustInfo = createSelector(selectCreateRequest, (state) => {
  return state.trustInfo;
});

export const selectTrustTaxCategoriesAndCodes = createSelector(selectTrustInfo, (state) => {
  return state.trustTaxCategoriesAndCodes;
});

export const selectTrustTaxCategories = createSelector(selectTrustTaxCategoriesAndCodes, (state) => {
  return Object.keys(state).filter(category => category !== ' ---Select--- ') as TrustTaxCategory[];
});

export const selectTrustTaxCodes = createSelector(
  selectTrustTaxCategoriesAndCodes,
  selectTransactionFormTransactionDetails,
  (categoriesAndCodes, details) => {
    if (details?.trustTaxCategory) {
      return categoriesAndCodes[details.trustTaxCategory as TrustTaxCategory] ?? [];
    }
    return [];
  })

export const selectTrustIncomePercent = createSelector(selectTransactionForm, (state) => {
  return state.transactionDetails.trustIncomePercent ?? Constants.oneHundredPercent;
});

export const selectTrustPrincipalPercent = createSelector(selectTransactionForm, (state) => {
  return state.transactionDetails.trustPrincipalPercent ?? Constants.zeroPercent;
});

export const selectTrustIncomeToPercent = createSelector(selectTransactionFormTransactionDetails, selectInternalTransferInfo, (state, itInfo) => {
  if (state && isItTransactionDetails(state)) {
    return (state).trustIncomeToPercent || itInfo.toAccountInfo.toIncome;
  }
  return '';
});

export const selectTrustPrincipalToPercent = createSelector(selectTransactionFormTransactionDetails, selectInternalTransferInfo, (state, itInfo) => {
  if (state && isItTransactionDetails(state)) {
    return (state).trustPrincipalToPercent || itInfo.toAccountInfo.toPrincipal;
  }
  return '';
});

export const selectPtrsInfo = createSelector(selectCreateRequest, (state) => {
  return state.ptrsInfo;
});

export const selectPtrsInfoPaidFor = createSelector(selectPtrsInfo, (state) => {
  return state.paidFor;
});

export const selectWireInfo = createSelector(selectCreateRequest, (state) => {
  return state.wireInfo;
});

export const selectWireHasCustodianRole = createSelector(selectWireInfo, (state) => {
  return state.hasCustodianRole;
})

export const selectHasNDFALPFA = createSelector(selectWireInfo, (state) => {
  return state.hasNDFALPFA;
})

export const selectStandingAuthCallSucceeded = createSelector(
  selectWireInfo,
  (state) => {
    return state.standingAuthCallSucceeded;
  }
)

export const selectItStandingAuthCallSucceeded = createSelector(
  selectInternalTransferInfo,
  (state) => {
    return state.standingAuthCallSucceeded;
  }
)

export const selectCheckInfo = createSelector(selectCreateRequest, (state) => {
  return state.checkInfo;
});

export const selectCheckDistributionReasonCode = createSelector(
  selectCheckInfo,
  (checkInfo) => checkInfo.distributionReasonCode
);

export const selectJournalListPendForRelease = createSelector(
  selectInternalTransfersInfo,
  (journalsInfo) => {
    let authListResponse = journalsInfo.journalAuthResponse;
    let authResponse: boolean = false;
    Object.keys(authListResponse).forEach((toAccountId) => {
      if (authListResponse[toAccountId].pendForRelease) {
        authResponse = true;
      }
    })
    return authResponse;
  }
)

export const selectJournalListPendForReleaseReasonEmpty = createSelector(
  selectInternalTransfersInfo,
  (journalsInfo) => {
    let authListResponse = journalsInfo.journalAuthResponse;
    let rulesReturned: boolean = false;
    Object.keys(authListResponse).forEach((toAccountId) => {
      if (authListResponse[toAccountId].pendForReleaseReason !== '') {
        rulesReturned = true
        }
    })
    return rulesReturned;
  }
)

export const selectJournalListCanRelease = createSelector(
  selectInternalTransfersInfo,
  (journalsInfo) => {
    let authListResponse = journalsInfo.journalAuthResponse;
    let canRelease: boolean = false;
    Object.keys(authListResponse).forEach((toAccountId) => {
      if (authListResponse[toAccountId].canRelease) {
        canRelease = true;
      }
    })
    return canRelease;
  }
)

export const selectPendForRelease = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectAchInfo,
  selectCheckInfo,
  selectInternalTransferInfo,
  selectJournalListPendForRelease,
  (type, wireInfo, achInfo, checkInfo, journalInfo, journalListPendForRelease) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.pendForRelease;
      case transactionTypes.ach:
        return achInfo.authResponse?.pendForRelease;
      case transactionTypes.check:
        return checkInfo.authResponse?.pendForRelease;
      case transactionTypes.internalTransfer:
        return journalInfo.journalAuthResponse?.pendForRelease || journalListPendForRelease;
      default:
        return false;
    }
  }
);



// returns true if there are no rules returned wich we will use along custom rules flag being on to decide if there is no I&R flow
export const selectPendForReleaseCustomRule = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectAchInfo,
  selectCheckInfo,
  selectInternalTransferInfo,
  selectJournalListPendForReleaseReasonEmpty,
  selectInternalTransfersInfo,
  (type, wireInfo, achInfo, checkInfo, journalInfo, journalListPendForReleaseReasonEmpty, internalTransfersInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.pendForReleaseReason === '';
      case transactionTypes.ach:
        return achInfo.authResponse?.pendForReleaseReason === '';
      case transactionTypes.check:
        return checkInfo.authResponse?.pendForReleaseReason === '';
      case transactionTypes.internalTransfer:
        if(internalTransfersInfo.positionsLotDetails.costMethod !== ""){
          return !journalListPendForReleaseReasonEmpty
        }
        return journalInfo.journalAuthResponse?.pendForReleaseReason === '';
      default:
        return false;
    }
  }
);

export const selectCanRelease = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectAchInfo,
  selectCheckInfo,
  selectInternalTransferInfo,
  selectJournalListCanRelease,
  (type, wireInfo, achInfo, checkInfo, journalInfo, journalListCanRelease) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.authResponse?.canRelease;
      case transactionTypes.ach:
        return achInfo.authResponse?.canRelease;
      case transactionTypes.check:
        return checkInfo.authResponse?.canRelease;
      case transactionTypes.internalTransfer:
        return journalInfo.journalAuthResponse?.canRelease || journalListCanRelease;
      default:
        return false;
    }
  }
);

export const selectCheckServiceError = createSelector(selectCheckInfo, (state) => {
  return state.serviceError;
});

export const selectCheckInternationalFedTaxError = createSelector(selectCheckInfo, (state) => {
  return state?.serviceError.toLowerCase() === fedTaxErrorMap.cashieringinternationFedTaxError.toLowerCase();
});

export const selectWireInternationalFedTaxError = createSelector(selectTransactionForm, (state) => {
  let hasWireInternationalFedTaxError = false;
  let verifyErrors = state.serviceError.errorList;
  verifyErrors?.forEach(error => {
    if (error.detail.toLowerCase() === fedTaxErrorMap.cashieringinternationFedTaxError.toLowerCase()) {
      hasWireInternationalFedTaxError = true;
    }
  })
  return hasWireInternationalFedTaxError;
});

export const selectInternalTransferInternationalFedTaxError = createSelector(selectInternalTransferInfo, (state) => {
  return state?.verifyErrorText?.toLowerCase() === fedTaxErrorMap.cashieringInternalTransferInternationalFedTaxError.toLowerCase();
})

export const selectCheckProcessDate = createSelector(selectCheckInfo,
  (state) => {
    return state.processDate;
  }
);

export const selectAchDefaultProcessDate = createSelector(selectAchInfo, (state) => {
  return state.defaultProcessDate;
});

export const selectAchProcessDate = createSelector(selectAchInfo, (state) => {
  return state.processDate;
})

export const selectIsThirdParty = createSelector(
  selectCheckInfo,
  (state) => {
    return state.isThirdParty;
  }
)

export const selectCheckStandingAuthCallSucceeded = createSelector(
  selectCheckInfo,
  (state) => {
    return state.standingAuthCallSucceeded;
  }
)

export const selectCheckIsRmdEligible = createSelector(
  selectCheckInfo,
  (state) => {
    return state.isRmdEligible;
  }
);

export const selectCheckRmdAnnualRemainingAmount = createSelector(
  selectCheckInfo,
  (state) => {
    return state.rmdAnnualRemainingAmount;
  }
);

export const selectCheckStwProcessCode = createSelector(selectCheckInfo, (state) => {
  return state.stwProcessCode;
});

export const selectCheckDistributionReason = createSelector(selectCheckInfo, (state) => {
  return state.distributionReason;
});


export const selectCheckTaxWithholding = createSelector(
  selectCheckInfo,
  selectUseStandingAuth,
  selectIsSloaOneTimeTotalDist,
  selectIsBundledOneTimeTotalDist,
  selectCheckDistributionReason,
  (state, useStandingAuth, isSloaOneTimeTotalDist, isBundledOneTimeTotalDist, distributionReason) => {
  let isQcdOrQualifiedSloaOrBsloaOneTimeTotalDist = useStandingAuth && (isSloaOneTimeTotalDist || isBundledOneTimeTotalDist) && (distributionReason?.description === distributionReasons.qualified || distributionReason?.description === distributionReasons.qcd);
  const taxWithholding: TaxWithholdingFields = {
    fedTaxPercent: isQcdOrQualifiedSloaOrBsloaOneTimeTotalDist ? '0' : state.fedTaxRate,
    fedTaxAmount: state.fedTaxAmount,
    fedTaxAmountFormatted: state.fedTaxAmountFormatted,
    stateTaxPercent: isQcdOrQualifiedSloaOrBsloaOneTimeTotalDist ? '0' : state.stateTaxRate,
    stateTaxAmount: state.stateTaxAmount,
    stateTaxAmountFormatted: state.stateTaxAmountFormatted,
    stateHasTaxes: state.stwProcessCode === 'I' ? true : false,
    distributionReason: state.distributionReason?.description,
    distributionReasonMap: state.distributionReasonsList,
  };
  return taxWithholding;
});

export const selectCheckDistributionReasonMap = createSelector(selectCheckInfo, (state) => {
  return state.distributionReasonsList;
});

export const selectInternalTransferTaxWithholding = createSelector(selectInternalTransferInfo, (state) => {
  const taxWithholding: TaxWithholdingFields = {
    fedTaxPercent: state?.iraFields.fedTaxPercent,
    fedTaxAmount: state?.iraFields.fedTaxAmount,
    stateTaxPercent: state?.iraFields.stateTaxPercent,
    stateTaxAmount: state?.iraFields.stateTaxAmount,
    distributionReason: state?.iraFields.distributionReason?.description,
    distributionReasonMap: state?.iraFields.distributionReasonMap,
  };
  return taxWithholding;
})
export const selectStateList = createSelector(
  selectCheckInfo,
  selectStagedTransactionForm, (checkInfo, form) => {
    return [
      ...(checkInfo?.stateList ?? []),
      ...(form?.stateList ?? [])
    ]
  });

export const selectFrequencyList = createSelector(selectCheckInfo, (state) => {
  return state.frequencyList;
});

export const selectWireHasEAuthAccess = createSelector(selectWireInfo, (state) => {
  return state.eauthAccess;
});

export const selectWireClientAuthNeeded = createSelector(selectWireInfo, (state) => {
  const isPalAccountEauthRequired = state.eauthAccess && state.eAuthModel?.eauthInd;
  return state.clientAuthorizationNeeded || isPalAccountEauthRequired;
});

export const selectCheckDisplayClientAuthSection = createSelector(selectCheckInfo, (state) => {
  return state.displayClientAuthorizationSection;
});

export const selectCheckClientAuthNeeded = createSelector(selectCheckInfo, (state) => {
  return state.clientAuthorizationNeeded;
});

export const selectNickname = createSelector(
  selectStagedTransactionType,
  selectInternalTransferInfo,
  selectCheckInfo,
  selectWireInfo,
  selectAchInfo,
  (type, itInfo, checkInfo, wireInfo, achInfo) => {
    switch (type) {
      case transactionTypes.internalTransfer:
        return itInfo.nickname;
      case transactionTypes.check:
        return checkInfo.nickName;
      case transactionTypes.wire:
        return wireInfo.nickname;
      case transactionTypes.ach:
        return achInfo.nickName;
      default:
        return '';
    }
  }
);

export const selectEauthModel = createSelector(
  selectTransactionType,
  selectInternalTransferInfo,
  selectWireInfo,
  selectCheckInfo,
  (type, itInfo, wireInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.internalTransfer:
        return itInfo.eAuthModel;
      case transactionTypes.wire:
        return wireInfo.eAuthModel;
      case transactionTypes.check:
        return checkInfo.eAuthModel;
      default:
        return new EAuthModel();
    }
  }
);

export const selectSpinnerStateForWireDetailsCall = createSelector(selectWireInfo, (state) => {
  return state.loading;
});

export const selectWireDetailsError = createSelector(selectWireInfo, (state) => {
  return state.abaMessageFields?.errorList[0]?.messageText;
});

export const selectWireDetailsWarning = createSelector(selectWireInfo, (state) => {
  return state.abaMessageFields?.warningList[0]?.messageText;
});



export const selectWireInputV3CallFinished = createSelector(selectWireInfo, (state) => {
  return state.inputCallV3Finished;
});

export const selectWireInputV3CallFailed = createSelector(selectWireInfo, (state) => {
  return state.inputCallV3Failed;
});

export const selectPalAgreementText = createSelector(selectWireInfo, (state) => {
  return state.palAgreementText ?? '';
});

export const selectPalAgreementAcknowledgement = createSelector(selectWireInfo, (state) => {
  return state.palAgreementAcknowledgement ?? '';
});

export const selectCheckInputCallFinished = createSelector(selectCheckInfo, (state) => {
  return state.inputCallFinished;
});

export const selectCheckInputCallSucceeded = createSelector(selectCheckInfo, (state) => {
  return state.inputCallSucceeded;
});

export const selectCheckVerifyFinished = createSelector(selectCheckInfo, (state) => {
  return state.verifyCallFinished;
});

export const selectInquiryCallFailed = createSelector(selectTransactionFormServiceError, (state) => {
  return (state.name == ApiEndpointType.checkInquiry ||
    state.name == ApiEndpointType.wireInquiry ||
    state.name == ApiEndpointType.internalTransferInquiry ||
    state.name == ApiEndpointType.moneylinkInquiry)
});

export const selectDefaultPayee = createSelector(selectCheckInfo, (state) => {
  return state.defaultPayee;
});

export const selectCheckPayee = createSelector(selectCheckInfo, (state) => {
  return state.payee;
})

export const selectDefaultRecipient = createSelector(selectCheckInfo, (state) => {
  return state.defaultRecipient.length > 0 ? state.defaultRecipient : [];
});

export const selectMailAddressDetails = createSelector(selectCheckInfo, (state) => {
  return state.mailAddressDetails;
});

export const selectAddressDetails = createSelector(selectCheckInfo, (state) => {
  return state.recipient;
});

export const selectFirmDetails = createSelector(selectCheckInfo, (state) => {
  return state.firmDetails;
});

export const selectCheckFeeDetails = createSelector(selectCheckInfo, (state) => {
  return state.checkFeeDetails;
});

export const selectCheckReasonMap = createSelector(selectCheckInfo, (state) => {
  return state.checkReasonMap;
});

export const selectCheckFee = createSelector(selectCheckInfo, (state) => {
  return state.checkFee;
});

export const selectCashieringIraAgreementText = createSelector(selectInternalTransferInfo, selectWireInfo, selectCheckInfo, selectAchInfo,
  (itInfo, wireInfo, checkInfo, achInfo) => {
    return itInfo?.cashieringIraAgreementText || wireInfo?.cashieringIraAgreementText || achInfo?.cashieringIraAgreementText || checkInfo?.cashieringIraAgreementText || cashieringIraAgreementText;
  });

export const selectReceivingAccountHolder = createSelector(selectWireInfo, (state) => {
  return state.receivingAccountHolder;
});

export const selectReceivingAccountHolderIso = createSelector(selectWireInfo, (state) => {
  return state.receivingAccountHolderIso;
});

export const selectTaxRemittance = createSelector(selectWireInfo, (state) => {
  return state.taxRemittance;
});

export const selectRecipientSelection = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isWireTransactionDetails(state)) {
    return state.recipient || '';
  }
  return '';
});

export const selectWireEscrowSelection = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isWireTransactionDetails(state)) {
    return state.isEscrow || false;
  }
  return false;
});

export const selectWirePayeeName = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isWireTransactionDetails(state)) {
    if (state.recipient === 'thirdParty') {
      return state.name
    } else {
      return state.payeeName || '';
    }
  }
  return '';
});

export const selectWireIntermediarySelection = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isWireTransactionDetails(state)) {
    return state.intermediarySelection || '';
  }
  return '';
});

export const selectPayeeType = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.payeeType || '';
  }
  return '';
});

export const selectThirdAddressee = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.addresseeNameCont2 || '';
  }
  return '';
});

export const selectStreetAddress = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.streetAddress || '';
  }
  return '';
});

export const selectStreetAddressCont = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.streetAddressCont || '';
  }
  return '';
});

export const selectCity = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.city || '';
  }
  return '';
});

export const selectCheckRecipientSelection = createSelector(selectTransactionFormTransactionDetails, (state) => {
  if (state && isCheckTransactionDetails(state)) {
    return state.recipient || '';
  }
  return '';
});

export const selectWireAbaCode = createSelector(selectWireInfo, (state) => {
  return state.abaStatusCode;
});

export const selectWireCountries = createSelector(selectWireInfo, (state) => {
  return state.countryList;
});

export const selectCheckCountries = createSelector(
  selectCheckInfo,
  selectStagedTransactionForm, (checkInfo, form) => {
    return [
      ...(checkInfo?.countryList ?? []),
      ...(form?.countryList ?? [])
    ]
  });

export const selectCountryCode = createSelector(selectCheckInfo, (state) => {
  return state.countryCode;
});

export const selectUseConstructedPayee = createSelector(selectCheckInfo, (state) => state.useConstructedPayee);

export const selectPendReasons = createSelector(
  selectAchInfo,
  selectWireInfo,
  selectInternalTransferInfo,
  selectCheckInfo,
  (achInfo, wireInfo, itInfo, checkInfo) => {
    return [
      ...(achInfo?.pendReasons ?? []),
      ...(itInfo?.pendReasons ?? []),
      ...(wireInfo?.pendReasons ?? []),
      ...(checkInfo?.pendReasons ?? [])
    ]
  });

export const selectConfirmationEAuthInfo = createSelector(
  selectWireInfo,
  selectInternalTransferInfo,
  selectInternalTransfersInfo,
  selectCheckInfo,
  (wireInfo, itInfo, itsInfo, checkInfo) => {
    return {
      eauthAccess: wireInfo.eauthAccess || itInfo.eauthAccess || itsInfo.multipleToJournalsList.some(eauthAccess => eauthAccess.eauthAccess) || checkInfo.displayClientAuthorizationSection,
      clientAuthorizationNeeded: wireInfo.clientAuthorizationNeeded || itInfo.clientAuthorizationNeeded || itsInfo.multipleToJournalsList.some(clientAuthorizationNeeded => clientAuthorizationNeeded.clientAuthorizationNeeded),
      accountRestricted: wireInfo.eAuthModel.accountRestricted || itInfo.eAuthModel.accountRestricted
    }
  })

export const selectIsPending = createSelector(
  selectPendReasons,
  selectIsBundled,
  (pendReasons, isBundled) => {
    if (isBundled) {
      return pendReasons.includes(pendReasonsStrings.taxElections);
    }
    return (pendReasons.length > 0);
  }
);

export const selectIsPendingForLoa = createSelector(
  selectCheckInfo,
  selectPendReasons,
  (checkInfo, pendReasons) => {
    return (pendReasons?.includes('Client Authorization Needed (LOA)') ||
      ((pendReasons?.includes('Schwab is Reviewing for Client Auth due to NDFA/LPFA') ||
        pendReasons?.includes('Client Authorization Needed For Tax Elections')) && !pendReasons?.includes('Electronic Authorization Needed')))
      && !checkInfo?.status?.description.includes('Cancelled');
  }
);

export const selectIsSubmitAndCreate = createSelector(
  selectCheckInfo,
  selectInternalTransferInfo,
  selectWireInfo,
  selectStagedTransactionType,
  (checkInfo, internalTransInfo, wireInfo, transactionType) => {
    switch (transactionType) {
      case transactionTypes.check:
        return checkInfo.isSubmitAndCreate
      case transactionTypes.internalTransfer:
        return internalTransInfo.isSubmitAndCreate
      case transactionTypes.wire:
        return wireInfo.isSubmitAndCreate || wireInfo.submitAndCreateToggledOn;
      default:
        return false;
    }
  }
);

export const selectAllSelectedEauthAccountHoldersHaveEmailAddress = createSelector(
  selectEAuthAccountHolderSelected,
  selectEAuthSelectedAccountHolderIds,
  selectEAuthClients,
  selectCreateStandingAuthEAuthClients,
  (isAccountHolderSelected, selectedAccountHolders, clients, standingAuthClients) => {
    if (isAccountHolderSelected) {
      var clientList = clients.eauthClients == undefined || clients.eauthClients.length == 0 ? standingAuthClients : clients;
      var selectedClients = clientList.eauthClients.filter(client => selectedAccountHolders.includes(client.customerId));
      var emailAddresses = selectedClients.map(client => client.emailAddress);
      if (emailAddresses.every(email => email.length > 0)) {
        return true;
      }
    }
    return false;
  }
)

export const selectAllEauthAccountHoldersHaveEmailAddress = createSelector(
  selectEAuthAccountHolderSelected,
  selectEAuthClients,
  selectCreateStandingAuthEAuthClients,
  (isAccountHolderSelected, clients, standingAuthClients) => {
    var clientList = clients.eauthClients == undefined || clients.eauthClients.length == 0 ? standingAuthClients : clients;
    if (isAccountHolderSelected) {
      var emailAddresses = clientList.eauthClients.map(client => client.emailAddress);
      if (emailAddresses.every(email => email.length > 0)) {
        return true;
      }
    }
    return false;
  }
)

export const selectEAuthAccountHolderWithFpoaOrFtaSelected = createSelector(
  selectEAuthAccountHolderSelected,
  selectEauthModel,
  selectEAuthSelectedAccountHolderIds,
  (isAccountHolderSelected, eauthModel, selectedAccountHolders) => {
    if (isAccountHolderSelected) {
      var clientList = eauthModel?.custAcctAuthorizationsList?.eAuthCustomerList?.filter(client => selectedAccountHolders?.includes(client.customerId));
      return clientList?.some(client => client.accountRoles.includes('FPOA') || client.accountRoles.includes('FTA'));
    }
    return false;
  }
)

export const selectStandingAuthEauthClientSelectedIsStandingAuthEligible = createSelector(
  selectCreateStandingAuthEAuthClients,
  selectEAuthAccountHolderSelected,
  selectEAuthSelectedAccountHolderIds,
  (clients, selected, selectedIds) => {
    if (selected) {
      var clientList = clients.eauthClients.filter(client => selectedIds.includes(client.customerId));
      return clientList.some(client => client.isEauthEligible);
    }
    return true;
  }
)

//only returns the eauth clients that are authorizing the transaction and/or standing auth
export const selectAllSelectedEauthClientsAwaitingAlliance = createSelector(
  selectIsSubmitAndCreate,
  selectEAuthClients,
  selectEAuthAccountHolderSelected,
  selectEAuthSelectedAccountHolderIds,
  selectEauthModel,
  (isSubmitAndCreate, clients, isAccountHolderSelected, selectedAccountHolders, eauthModel) => {
    var clientList = isSubmitAndCreate && !eauthModel.isOrgAccount ? clients.eauthClients :
      isAccountHolderSelected ? clients.eauthClients.filter(client => selectedAccountHolders.includes(client.customerId)) : [];
    return clientList.filter(client => client.eauthSetup.status === 'Awaiting Alliance setup');
  }
)

export const selectWarningList = createSelector(
  selectAchInfo,
  selectWireInfo,
  selectInternalTransferInfo,
  selectInternalTransfersInfo,
  selectCheckInfo,
  selectStagedTransactionType,
  (achInfo, wireInfo, itInfo, itsInfo, checkInfo, transactionType) => {
    if (transactionType === transactionTypes.ach) return achInfo?.messageFields?.warningList ?? [];
    if (transactionType === transactionTypes.internalTransfer)
      if (itsInfo?.multipleToJournalsList.length > 0) {
        return itsInfo?.multipleToJournalsList.flatMap(warningMessage => warningMessage.messageInfo.warningList) ?? []
      }
      else {
        return itInfo?.messageInfo?.warningList ?? []
      };
    if (transactionType === transactionTypes.wire) return wireInfo?.warningsList ?? [];
    if (transactionType === transactionTypes.check) return checkInfo?.messageInfo?.warningList ?? [];
    return [];
  });

export const selectHasSubmitAndCreateError = createSelector(
  selectWarningList,
  selectStagedTransactionType,
  selectIsSubmitAndCreate,
  selectHasAccessInitiateMoneyMovement,
  selectFeatureFlags,
  selectStandingAuthorization,
  (warningList, transactionType, isSubmitAndCreate, hasMmInitiateAccess, featureFlags, standingAuthorization) => {
    let mappedList: string[];

    if (warningList.length > 0 && typeof (warningList[0]) !== 'string') {
      mappedList = warningList.map((warning) => (warning as WarningOrError).messageText)
    } else {
      mappedList = warningList as string[]
    }

    if (mappedList.includes(submitAndCreateWarnings.submitFailCreateSuccess)) {
      return {
        hasError: true,
        transactionFailed: true,
        standingAuthFailed: false
      };
    }

    if (mappedList.includes(submitAndCreateWarnings.submitSuccessCreateFail)) {
      return {
        hasError: true,
        transactionFailed: false,
        standingAuthFailed: true,
      };
    }
    // In custom rules split wire submit-and-create, the main wire acknowledge can succeed while the
    // standing-auth acknowledge fails separately. Treat that second acknowledge failure
    // as a standing-auth submit-and-create error so confirmation shows the correct banner.
    if (transactionType === transactionTypes.wire
      && isSubmitAndCreate
      && hasMmInitiateAccess
      && featureFlags?.createSloaToggleForCreateRulesEnabled
      && standingAuthorization?.standingAuthorizationForm?.serviceError?.hasError
      && standingAuthorization?.standingAuthorizationForm?.serviceError?.name === ApiEndpointType.wireAcknowledgeV3) {
      return {
        hasError: true,
        transactionFailed: false,
        standingAuthFailed: true,
      };
    }

    return {
      hasError: false,
      standingAuthFailed: false,
      transactionFailed: false,
    }
  }
)
export const selectMappedPendReason = createSelector(
  selectPendReasons,
  selectEAuthAccountHolderSelected,
  selectIsSubmitAndCreate,
  selectHasSubmitAndCreateError,
  selectHasAccessInitiateMoneyMovement,
  selectCanRelease,
  selectStagedTransactionType,
  selectAchRecurringFrequency,
  (reasons, eAuthSelected, isSubmitAndCreate, submitAndCreateError, hasMmInitiateAccess, canRelease, transactionType, achRecurringFrequency) => {
    if (hasMmInitiateAccess && !canRelease) {
      return Constants.initiateInRTransaction;
    }
    if (isSubmitAndCreate) {
      if (submitAndCreateError.hasError) {
        if (submitAndCreateError.transactionFailed) {
          return submitAndCreateWarnings.submitFailCreateSuccess;
        }

        if (submitAndCreateError.standingAuthFailed) {
          return submitAndCreateWarnings.submitSuccessCreateFail;
        }
      }

      if (eAuthSelected) {
        return submitAndCreateConfirmationMessages.eauthNeeded;
      }

      return submitAndCreateConfirmationMessages.loaNeeded;
    }

    if (reasons.includes('Client Authorization Needed (LOA)')) {
      return Constants.loaNeeded;
    }
    if (reasons.includes('Electronic Authorization Needed')) {
      return Constants.eauthNeeded;
    }
    if (reasons.includes('Client Authorization Needed For Tax Elections') || reasons.includes('Schwab is Reviewing for Client Auth due to NDFA/LPFA')) {
      return eAuthSelected ? Constants.eauthNeeded : Constants.loaNeeded;
    }
    if (transactionType === transactionTypes.ach && !!achRecurringFrequency) {
      return Constants.newRecurringAchProfileConfirmation;
    }
    return Constants.defaultPendDescription;
  }
);

export const selectWireTaxWithholding = createSelector(selectWireInfo, (info) => {
  const taxWithholding: TaxWithholdingFields = {
    fedTaxPercent: info.fedTaxPercent,
    fedTaxAmount: info.fedTaxAmount,
    stateTaxPercent: info.stateTaxPercent,
    stateTaxAmount: info.stateTaxAmount,
    stateHasTaxes: info.stateHasTaxes,
    distributionReason: info.distributionReason.description,
    distributionReasonMap: info.distributionReasonMap
  };
  return taxWithholding;
})

export const selectWireProcessDateFields = createSelector(selectWireInfo, (info) => {
  return info.processDateFields;
})

export const selectWireAvailableCashMMF = createSelector(selectWireInfo, (info) => {
  return info.availableCashMMF;
});

export const selectWireAvailableCashMargin = createSelector(selectWireInfo, (info) => {
  return info.availableCashMargin;
});

export const selectCheckAvailableCashMMF = createSelector(selectCheckInfo, (info) => {
  return info.availableCashMmf;
});

export const selectCheckAvailableCashMargin = createSelector(selectCheckInfo, (info) => {
  return info.availableCashMargin;
});

export const selectPtrsCallFinished = createSelector(selectPtrsInfo, (info) => {
  return info.ptrsCallFinished;
});

export const selectWireVerifyV3CallFinished = createSelector(selectWireInfo, info => info.verifyV3CallFinished);

export const selectWireEAuthModel = createSelector(selectWireInfo, info => info.eAuthModel);

export const selectWireDistributionReason = createSelector(selectWireInfo, (state) => {
  return state.distributionReason;
});

export const selectWireDistributionReasonMap = createSelector(selectWireInfo, (state) => {
  return state.distributionReasonMap;
});

export const selectIsInRReleaseFlow = createSelector(selectHasAccessInitiateMoneyMovement, selectPendForRelease, (inrFSG, initiateFlow) => {
  return inrFSG && !initiateFlow;
})

export const selectInternalTransferVerifyCallFinished = createSelector(selectInternalTransferInfo, (state) => {
  return state.verifyCallFinished;
});

export const selectInternalTransfersVerifyCallFinished = createSelector(selectInternalTransfersInfo, selectIsInRReleaseFlow, (info, inrReleaseFlow) => {
  return info.verifyCallFinished || (inrReleaseFlow && info.verifyCallFinishedWithError);
});

export const selectVerifyCallFinished = createSelector(
  selectWireVerifyV3CallFinished,
  selectInternalTransferVerifyCallFinished,
  selectInternalTransfersVerifyCallFinished,
  selectCheckVerifyFinished,
  selectAchVerifyFinished,
  (wireVerifyFinished, internalTransferVerifyFinished, internalTransfersVerifyFinished, checkVerifyCallFinished, achVerifyCallFinished) => {
    return wireVerifyFinished || internalTransferVerifyFinished || internalTransfersVerifyFinished || checkVerifyCallFinished || achVerifyCallFinished;
  }
);

export const selectOpenTwePopupConditions = createSelector(
  selectVerifyCallFinished,
  selectTwePopupIndicator,
  (verifyCallFinished, openTwePopup) => {
    return {
      verifyCallFinished,
      openTwePopup
    }
  }
);

export const selectStateCode = createSelector(selectInternalTransferInfo, selectWireInfo, selectAchInfo, selectCheckInfo,
  (itInfo, wireInfo, achInfo, checkInfo) => {
    return itInfo?.stateCd || wireInfo?.stateCd || achInfo?.stateCd || checkInfo?.stateCd;
  }
);

export const selectStateTypeInd = createSelector(selectInternalTransferInfo, selectWireInfo, selectAchInfo, selectCheckInfo,
  (itInfo, wireInfo, achInfo, checkInfo) => {
    return itInfo?.inputIraFields?.stateTypeIndicator || wireInfo?.stateTypeIndicator || achInfo?.stateTypeIndicator || checkInfo?.stateTypeIndicator;
  }
);

export const selectStateTaxPercentDefault = createSelector(selectInternalTransferInfo, selectWireInfo, selectCheckInfo, selectAchInfo,
  (itInfo, wireInfo, checkInfo, achInfo) => {
    return itInfo?.inputIraFields?.stateTaxPercent || wireInfo?.inputIraFields?.stateTaxPercent ||
      checkInfo?.inputIraFields?.stateTaxAmountFormatted || achInfo?.defaultStateTaxPercent;
  }
);

export const selectFedTaxPercentDefault = createSelector(selectInternalTransferInfo, selectWireInfo, selectCheckInfo, selectAchInfo,
  (itInfo, wireInfo, checkInfo, achInfo) => {
    return itInfo?.inputIraFields?.fedTaxPercent || wireInfo?.inputIraFields?.fedTaxPercent ||
      checkInfo?.inputIraFields?.fedTaxPercent || achInfo?.defaultFedTaxPercent;
  }
);

export const selectStateAmountReview = createSelector(selectInternalTransferInfo, selectWireInfo, selectCheckInfo,
  (itInfo, wireInfo, checkInfo) => {
    return itInfo?.iraFields?.stateTaxPercent || wireInfo?.stateTaxPercent || checkInfo?.stateTaxRate;
  }
);


export const selectPendReasonsList = createSelector(
  selectInternalTransferInfo,
  selectCheckInfo,
  (itInfo, checkInfo) => {
    return [
      ...(itInfo?.pendReasons ?? []),
      ...(checkInfo?.pendReasonsListSet ?? [])
    ]
  });

export const selectWireCreditTo = createSelector(selectWireInfo, (state) => {
  return state.creditTo;
});

export const selectCanDisplayCreditToInfo = createSelector(selectWireInfo, (state) => {
  return state.canDisplayCreditToInfo;
})

export const selectIsFedWire = createSelector(selectWireInfo, (state) => {
  return state.isFedWire;
})

export const missingThirdPartyAddress = createSelector(selectWireInfo, (state) => {
  return state.missingThirdPartyAddress;
});

export const missingIntermediaryAddress = createSelector(selectWireInfo, (state) => {
  return state.missingIntermediaryAddress;
});

export const selectLoadToAccountError = createSelector(selectInternalTransferInfo, (state) => {
  return state.loadToAccountErrorText;
});

export const selectLoadToAccountsError = createSelector(selectInternalTransfersInfo, (state) => {
  return Object.values(state.toAccountPropertiesById).find(toAccountProperty => toAccountProperty.loadToAccountErrorText)?.loadToAccountErrorText ?? "";
});

export const selectInternalTransfersToAccountProperties = createSelector(selectInternalTransfersInfo, (state) => {
  return state.toAccountPropertiesById;
});

export const selectVerifyError = createSelector(selectInternalTransferInfo, (state) => {
  return state.verifyErrorText;
});

export const selectVerifyErrorsByToAccount = createSelector(selectInternalTransfersInfo, (state) => {
  var errors: string[] = [];
  return Object.entries(state.toAccountPropertiesById).reduce((errors, [key, value]) => {
    if (value.hasError && value.verifyErrorText) {
      var index = errors.findIndex((error: string) => error.length && error.includes(value.verifyErrorText));
      if (index !== -1) {
        errors[index] = errors[index] + ', ' + key
      }
      else {
        errors.push(value.verifyErrorText + ': ' + key);
      }
    }
    return errors;
  }, errors);
});

export const selectItContributionTypeMap = createSelector(selectInternalTransferInfo, (state) => {
  return state.iraContributionTypeMap;
});

export const selectCurrentTransaction = createSelector(selectTransactionsTableState, (state) => {
  return state.currentTransaction;
});

export const selectCurrentTemplate = createSelector(selectTransactionsTableState, (state) => {
  return state.currentTemplate;
});

export const selectIsEditableCurrentTransaction = createSelector(selectCurrentTransaction, (state) => {
  return state?.IsEditable;
})

export const selectIsCancelableCurrentTransaction = createSelector(selectCurrentTransaction, (state) => {
  return state?.IsCancelable;
});

export const selectIsAchDistributionUpdatedByVerify = createSelector(selectAchInfo, (state) => {
  return state.distributionReasonUpdated;
});

export const selectIsAchGrossNetUpdatedByVerify = createSelector(selectAchInfo, (state) => {
  return state.grossNetUpdated;
})

export const selectIsPendingAuthorization = createSelector(
  selectStagedTransactionType,
  selectInternalTransferInfo,
  selectInternalTransfersInfo,
  selectWireInfo,
  selectCheckInfo,
  (type, itInfo, itsInfo, wireInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.internalTransfer:
        return itInfo.clientAuthorizationNeeded || itsInfo.multipleToJournalsList.some(clientAuth => clientAuth.clientAuthorizationNeeded)
      case transactionTypes.wire:
        return wireInfo.clientAuthorizationNeeded;
      case transactionTypes.check:
        return checkInfo.clientAuthorizationNeeded;
      default:
        return false;
    }
  });

export const selectUseStandingAuthFromStaged = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && state.extraPrompts && (isItExtraPrompts(state.extraPrompts)
      || isWireExtraPrompts(state.extraPrompts)
      || isCheckExtraPrompts(state.extraPrompts)
    )) {
      return state?.extraPrompts?.useStandingAuth?.toLowerCase() === 'yes';
    }
    return false;
  }
)

export const selectUseBundledStandingAuthFromStaged = createSelector(
  selectStagedTransactionForm,
  (state) => {
    if (state && state.extraPrompts &&
      (isWireExtraPrompts(state.extraPrompts)
        || isCheckExtraPrompts(state.extraPrompts)
        || isItExtraPrompts(state.extraPrompts))
    ) {
      return state?.extraPrompts?.useBundledStandingAuth?.toLowerCase() === 'yes';
    }
    return false;
  }
)

export const selectUseStandingAuthVal = createSelector(
  selectTransactionFormExtraPrompts,
  selectWireUseStandingAuthVal,
  selectJournalsUseStandingAuth,
  selectCheckUseStandingAuth,
  (state, wireVal, journalVal, checkVal) => {
    if (state && isItExtraPrompts(state)) {
      return journalVal;
    }
    if (state && isWireExtraPrompts(state)) {
      return wireVal;
    }
    if (state && isCheckExtraPrompts(state)) {
      return checkVal;
    }
    else {
      return '';
    }
  }
);

export const selectIsPositionsFromStaged = createSelector(
  selectStagedTransactionForm,
  (form) => {
    if (form.extraPrompts && 'transferType' in form.extraPrompts) {
      return form.extraPrompts.transferType === 'positions' || form.extraPrompts.transferType === 'positionsCash';
    }
    return false;
  }
)


/**
 * This selector is meant to be used to help determine whether to show the Submit and Create section on the review page.
 * We don't need to look at if it's create standing auth because create standing auth has its own review page
 */
export const selectIsUseStandingAuth = createSelector(
  selectUseStandingAuthFromStaged,
  (isUseStandingAuth) => isUseStandingAuth
);

export const selectIsPtrsIt = createSelector(
  selectInternalTransferInfo,
  (info) => {
    return info.trustFields.isTrust;
  }
);

export const selectWireIsPendingOnlyForCTWE = createSelector(
  selectWireInfo,
  (info) => {
    return info.isPendingOnlyForCTWE;
  }
);

export const selectEauthDPOARole = createSelector(
  selectEauthModel,
  (info) => {
    if (info.custAcctAuthorizationsList?.eAuthCustomerList && info.custAcctAuthorizationsList?.eAuthCustomerList.some((customer: EAuthModelCustomer) => customer?.accountRoles)) {
      if (info.custAcctAuthorizationsList?.eAuthCustomerList.some((customer: EAuthModelCustomer) => customer?.accountRoles.some(s => s.includes("DPOA")))) {
        return true;
      }
      else {
        false;
      }
    } else {
      return false;
    }
  }
);

export const selectIsPensionTrustAccount = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectInternalTransferInfo,
  selectCheckInfo,
  (type, wireInfo, journalInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.regShortName === 'Pen Trust';
      case transactionTypes.internalTransfer:
        return journalInfo.accountInfoBanner.regShortName === 'Pen Trust';
      case transactionTypes.check:
        return checkInfo.regShortName === 'Pen Trust';
      default:
        return false;
    }
  });

export const selectIsTestTrustAccount = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectInternalTransferInfo,
  selectCheckInfo,
  (type, wireInfo, journalInfo, checkInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.regShortName === 'Test Trust';
      case transactionTypes.internalTransfer:
        return journalInfo.accountInfoBanner.regShortName === 'Test Trust';
      case transactionTypes.check:
        return checkInfo.regShortName === 'Test Trust';
      default:
        return false;
    }
  });
/*
 * Please review selectors.spec unit test cases for this flow before modifying the behavior
*/
export const selectShowSubmitAndCreate = createSelector(
  selectIsUseStandingAuth,
  selectUseBundledStandingAuthFromStaged,
  selectCheckDistributionReason,
  selectStagedTransactionType,
  selectIsPositionsFromStaged,
  selectClientAuthNeeded,
  selectIsPtrsIt,
  selectWireClientAuthNeeded,
  selectWireIsPendingOnlyForCTWE,
  selectWireUseStandingAuthVal,
  selectCheckUseStandingAuth,
  selectJournalsUseStandingAuth,
  selectEauthDPOARole,
  selectIsRecurring,
  selectPendForRelease,
  selectHasAccessInitiateMoneyMovement,
  selectHasAccessReleaseMoneyMovement,
  selectCanRelease,
  selectIsPensionTrustAccount,
  selectEAuthAccountHolderWithFpoaOrFtaSelected,
  selectIsTestTrustAccount,
  (
    isUseStandingAuth,
    isUseBundledStandingAuth,
    distReason,
    transactionType,
    isPositions,
    itClientAuthNeeded,
    isPtrs,
    wireClientAuthNeeded,
    wirePendForCTWE,
    wireStandingAuthVal,
    checkStandingAuthVal,
    journalStandingAuthVal,
    dpoaRole,
    isRecurring,
    needToPendForRelease,
    hasInitiateAccess,
    hasReleaseAccess,
    canRelease,
    isPensionTrustAccount,
    fpoaOrFtaSelected,
    IsTestTrustAccount
  ) => {
    const isAchTransaction = transactionType === transactionTypes.ach;

    // digital to pdf flow - show mm-options section
    if (transactionType === transactionTypes.check && IsTestTrustAccount) {
      return true;
    }

    // Early exit if it's an ach transaction or is using standing/bundledstanding auth
    // check and journal transactions can be authorized by DPOA when enabled
    if (isAchTransaction || isUseStandingAuth || isUseBundledStandingAuth || isPensionTrustAccount) {
      return false;
    }
    
    if (fpoaOrFtaSelected && (hasInitiateAccess || hasReleaseAccess)) { // Dynamically show or hide based on FPOA/FTA selection when the customRules flag and createSLOAToggle flag are enabled, and on the initiator review page
        return false;
      }

    // Early computation to simplify the logic
    const isCheckStandingAuth = !(checkStandingAuthVal === 'bundled-no' || checkStandingAuthVal === 'no');
    const isJournalStandingAuth = !(journalStandingAuthVal === 'bundled-no' || journalStandingAuthVal === 'no');

    if (transactionType === transactionTypes.check) {
      if (distReason.subCode === 'RMD' || isCheckStandingAuth || (isRecurring && !isCheckStandingAuth)) {
        return false;
      }
    }
    else if (transactionType === transactionTypes.internalTransfer) {
      if (isPositions || !itClientAuthNeeded) {
        return false;
      }

      if (isJournalStandingAuth || (isPtrs && !journalStandingAuthVal.includes('bundled')) || (isRecurring && !isJournalStandingAuth)) {
        return false;
      }
    }
    else if (transactionType === transactionTypes.wire) {
      if (wirePendForCTWE || (!wireClientAuthNeeded && !(wireStandingAuthVal === 'bundled-no'))) {
        return false;
      }
    }
    // for '' transaction type return false
    else {
      return false;
    }

    return true;
  }
);

export const selectSubmitAndCreateEauthIneligible = createSelector(selectEauthModel, (eauthModel) => {
  return !eauthModel.isOrgAccount && !eauthModel.eauthSLOAInd;
});

export const selectTransactionOptions = createSelector(selectCreateRequest, (state) => {
  return state.transactionOptions;
});

export const selectSubmitAndCreateToggledOn = createSelector(selectTransactionOptions, (options) => {
  return options.submitAndCreateToggledOn;
});

export const selectSaveAsTemplateToggledOn = createSelector(selectTransactionOptions, (options) => {
  return options.saveAsTemplateToggledOn;
});

export const selectTemplateNickname = createSelector(selectTransactionOptions, (options) => {
  return options.templateNickname;
});

export const selectTemplateNicknameFromModal = createSelector(selectCreateRequest, (state) => {
  return state.saveAsTemplateModal.templateNickname;
});

export const selectIsInputCallFinishedMultiple = createSelector(
  selectInternalTransfersInfo,
  (state) => {
    return state.inputCallFinished;
  });

export const selectInputAmount = createSelector(selectTransactionFormTransactionDetails, (state) => {
  return state.amount;
});

export const selectAmountType = createSelector(selectTransactionFormTransactionDetails, (state) => {
  return state.amountType;
});

export const selectIsISOCompliant = createSelector(selectStagedTransactionForm, (stagedTransactionForm) => {
  if (stagedTransactionForm.transactionDetails && isWireTransactionDetails(stagedTransactionForm.transactionDetails)) {
    return stagedTransactionForm.transactionDetails.isISOCompliant || false;
  }
  return false
});

export const selectRevalidationStatus = createSelector(
  selectStagedTransactionType,
  selectWireInfo,
  selectInternalTransferInfo,
  selectCheckInfo,
  selectAchInfo,
  selectMultipleToJournalsList,
  (type, wireInfo, journalInfo, checkInfo, achInfo, multipleToAccountJournalInfo) => {
    switch (type) {
      case transactionTypes.wire:
        return wireInfo.revalidationStatus;
      case transactionTypes.internalTransfer:
        if (multipleToAccountJournalInfo.length > 0) {
          return multipleToAccountJournalInfo[0].revalidation?.status;
        } else {
          return journalInfo.revalidationStatus;
        }
      case transactionTypes.check:
        return checkInfo.revalidation?.status;
      case transactionTypes.ach:
        return achInfo.revalidation?.status;
      default:
        return '';
    }
  });

export const selectAchInfoParty = createSelector(selectAchInfo, (state) => {
  return state.thirdPartyInd;
});

export const isRestrictedToAccountEntered = createSelector(
  selectInternalTransfersInfo,
  selectLoadToAccountsError,
  (state, loadToAccountsError) => {
    let regType: string = '';
    Object.values(state.toAccountInfo).some(value => {
      regType = value?.registrationType;
    });
    Object.values(state.toAccountPropertiesById).some(value => {
      loadToAccountsError = value?.loadToAccountErrorText ?? '';
    });
    loadToAccountsError = loadToAccountsError ?? '';
    let isCustodialToAccount: boolean = regType === RegTypeCode.CUSTODIAL;

    if (loadToAccountsError != '') { // In reducer isToAccountFailure have errorText,isToAccountSucess assiging empty string
      return true;
    }
    // Going-digital flag removed; custodial accounts are now allowed for internal transfers
    return false;
  }
);

export const selectRevalidationResponse = createSelector(
  selectStagedTransactionType,
  selectCheckInfo,
  selectAchInfo,
  selectWireInfo,
  selectInternalTransferInfo,
  selectMultipleToJournalsList,
  (type, checkInfo, achInfo, wireInfo, itInfo, multipleToAccountItInfo) => {
    var revalidation: Revalidation = {
      status: '',
      errors: [],
      fieldsUpdated: []
    };
    switch (type) {
      case transactionTypes.check:
        return checkInfo.revalidation;
      case transactionTypes.ach:
        return achInfo.revalidation;
      case transactionTypes.wire:
        return wireInfo.revalidation;
      case transactionTypes.internalTransfer:
        if (multipleToAccountItInfo.length > 0) {
          return multipleToAccountItInfo[0].revalidation;
        } else {
          return itInfo.revalidation;
        }
      default:
        return revalidation;
    }
  }
);
